/**
 * AtlasContextAdapterOrganism
 *
 * Bridges context execution data to the Atlas rendering component.
 * Fetches a context view config and context execution results,
 * then maps them to Atlas-compatible props.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { useContextData } from '../../../hooks/useContextData';
import { useContextView } from '../../../hooks/useContextView';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { ContextViewRecord } from '../../../api/contextApi';
import type { SphereData, CacheMetadata } from '../../../hooks/useContextData';

export interface AtlasContextAdapterOrganismProps {
  /** API clients from context */
  apiClients: ApiClients | null;
  /** Context view system name to load */
  viewSystemName: string;
  /** Root sphere ID to execute context from */
  rootSphereId: string;
  /** Results per page per level */
  limit?: number;
  /** Max depth for repeat levels */
  depth?: number;
  /** Callback when a sphere node is selected */
  onNodeSelect?: (sphereId: string) => void;
  /** Callback when chat bubble is clicked */
  onChatBubbleClick?: (sphereId: string) => void;
  /** Custom render for loading state */
  renderLoading?: () => React.ReactNode;
  /** Custom render for error state */
  renderError?: (error: string) => React.ReactNode;
  /** CSS class name */
  className?: string;
}

/** Mapped data for Atlas consumption */
export interface AtlasContextData {
  /** View config from schema-service */
  view: ContextViewRecord;
  /** Spheres organized by column index */
  spheresByColumn: Record<number, SphereData[]>;
  /** Level metadata */
  levels: Array<{ id: string; order: number; label: string; direction: string; count: number }>;
  /** Total loaded count */
  totalLoaded: number;
  /** Cache metadata */
  cacheMetadata: CacheMetadata | null;
  /** Refresh handler (bypasses cache) */
  refresh: () => Promise<void>;
  /** Load more handler */
  fetchMore: () => Promise<void>;
  /** Whether more data is available */
  hasMore: boolean;
}

const AtlasContextAdapterOrganism: React.FC<AtlasContextAdapterOrganismProps> = ({
  apiClients,
  viewSystemName,
  rootSphereId,
  limit,
  depth,
  onNodeSelect,
  onChatBubbleClick,
  renderLoading,
  renderError,
  className,
}) => {
  const { t } = useTranslation();
  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const contextSystemName = view?.context_ref ?? '';

  const {
    data,
    loading: dataLoading,
    error: dataError,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    limit,
    depth,
  });

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  const _atlasConfig = useMemo(() => {
    if (!view) return null;
    const vc = view.view_config;
    return {
      showTraversalArrows: vc.showTraversalArrows as boolean | undefined,
      allowTraversalDirectionChange: vc.allowTraversalDirectionChange as boolean | undefined,
      showNearbyNodeCount: vc.showNearbyNodeCount as boolean | undefined,
      lockFocus: vc.lockFocus as boolean | undefined,
      hideAddSphere: vc.hideAddSphere as boolean | undefined,
      columns: vc.columns as Array<Record<string, unknown>> | undefined,
      mobileBreakpoint: vc.mobileBreakpoint as number | undefined,
      compactColumns: vc.compactColumns as string[] | undefined,
    };
  }, [view]);

  if (loading && !data) {
    if (renderLoading) return <>{renderLoading()}</>;
    return <div className={className}>{t('context.common.loadingData')}</div>;
  }

  if (error && !data) {
    if (renderError) return <>{renderError(error)}</>;
    return <div className={className}>{t('context.common.error', { message: error })}</div>;
  }

  if (!data || !view) {
    return null;
  }

  return (
    <div className={className} data-testid="atlas-context-adapter">
      <div className="atlas-context-adapter__header">
        <span className="atlas-context-adapter__title">
          {view.name?.default ?? view.system_name}
        </span>
        <button
          className="atlas-context-adapter__refresh"
          onClick={refresh}
          title={t('context.common.refreshData')}
          type="button"
        >
          ↻
        </button>
      </div>
      <div className="atlas-context-adapter__content">
        {Object.entries(data.spheresByColumn).map(([colIndex, spheres]) => (
          <div key={colIndex} className="atlas-context-adapter__column">
            <div className="atlas-context-adapter__column-header">
              {data.context.levels[Number(colIndex)]?.label ?? t('context.common.level', { index: colIndex })}
              <span className="atlas-context-adapter__column-count">
                ({spheres.length})
              </span>
            </div>
            <div className="atlas-context-adapter__nodes">
              {spheres.map((sphere) => (
                <button
                  type="button"
                  key={sphere.id}
                  className="atlas-context-adapter__node"
                  onClick={() => onNodeSelect?.(sphere.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onNodeSelect?.(sphere.id);
                  }}
                  tabIndex={0}
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%', font: 'inherit', color: 'inherit' }}
                >
                  <span className="atlas-context-adapter__node-label">
                    {String(sphere.label ?? sphere.id)}
                  </span>
                  {onChatBubbleClick && (
                    <button
                      className="atlas-context-adapter__chat"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChatBubbleClick(sphere.id);
                      }}
                      title={t('context.common.openChat')}
                      type="button"
                    >
                      💬
                    </button>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          className="atlas-context-adapter__load-more"
          onClick={fetchMore}
          type="button"
        >
          {t('context.common.loadMore')}
        </button>
      )}
      {cacheMetadata && (
        <div className="atlas-context-adapter__cache-info" data-testid="cache-info">
          {cacheMetadata.structureHit ? t('context.common.cached') : t('context.common.fresh')} · {data.stats.totalLoaded} {t('context.common.spheres')}
        </div>
      )}
    </div>
  );
};

export default AtlasContextAdapterOrganism;
